import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 2 minutes before considering it stale
      staleTime: 2 * 60 * 1000,
      // Keep unused data in cache for 5 minutes (gcTime replaces cacheTime in v4+)
      gcTime: 5 * 60 * 1000,
      // Retry failed requests with smart logic
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors (client errors)
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
      // Disable refetch on window focus (reduces unnecessary API calls)
      refetchOnWindowFocus: false,
      // Don't refetch on reconnect by default
      refetchOnReconnect: false,
      // Only refetch on mount if data is stale
      refetchOnMount: 'stale',
    },
    mutations: {
      // Retry failed mutations once
      retry: 1,
    },
  },
});

/**
 * Query Keys Factory for BillingV2
 * Centralized query key management for type safety and consistency
 */
export const queryKeys = {
  // Billing summary queries
  billing: {
    all: ['billing'],
    summary: (caseId) => ['billing', 'summary', caseId],
  },
  // Expenses
  expenses: {
    all: ['expenses'],
    lists: () => ['expenses', 'list'],
    // Token-based pagination (old)
    list: (filters, pageLimit, pagination_token, pagination_direction, next) => [
      'expenses',
      'list',
      filters,
      pageLimit,
      pagination_token,
      pagination_direction,
      next,
    ],
    // RDS-based pagination (new)
    listV2: (filters, page, pageSize) => ['expenses', 'list', 'v2', { filters, page, pageSize }],
  },
  // Time entries
  timeEntries: {
    all: ['time-entries'],
    list: (filters, is_automated, pageLimit, pagination_token, pagination_direction, is_full_list) => [
      'time-entries',
      'list',
      filters,
      is_automated,
      pageLimit,
      pagination_token,
      pagination_direction,
      is_full_list,
    ],
    // RDS-based pagination (new)
    listV2: (filters, page, pageSize) => ['time-entries', 'list', 'v2', { filters, page, pageSize }],
  },
  // Payment requests
  paymentRequests: {
    all: ['payment-requests'],
    list: (filters, pageLimit, pagination_token, pagination_direction) => [
      'payment-requests',
      'list',
      filters,
      pageLimit,
      pagination_token,
      pagination_direction,
    ],
    // RDS-based pagination (new)
    listV2: (filters, page, pageSize) => ['payment-requests', 'list', 'v2', { filters, page, pageSize }],
  },
  // Invoices
  invoices: {
    all: ['invoices'],
    list: (filters) => ['invoices', 'list', filters],
    // RDS-based pagination (new)
    listV2: (filters, page, pageSize) => ['invoices', 'list', 'v2', { filters, page, pageSize }],
    detail: (invoiceId) => ['invoices', 'detail', invoiceId],
  },
  // Payment history
  paymentHistory: {
    all: ['payment-history'],
    list: (filters) => ['payment-history', 'list', filters],
    // RDS-based pagination (new)
    listV2: (filters, page, pageSize) => ['payment-history', 'list', 'v2', { filters, page, pageSize }],
  },
  // Trust accounting
  trustAccounting: {
    all: ['trust-accounting'],
    list: (filters) => ['trust-accounting', 'list', filters],
    // RDS-based pagination (new)
    listV2: (filters, page, pageSize) => ['trust-accounting', 'list', 'v2', { filters, page, pageSize }],
  },
  // Operating accounting
  operatingAccounting: {
    all: ['operating-accounting'],
    list: (filters) => ['operating-accounting', 'list', filters],
  },
  // Temporary funds
  temporaryFunds: {
    all: ['temporary-funds'],
    list: (filters) => ['temporary-funds', 'list', filters],
    // RDS-based pagination (new)
    listV2: (filters, page, pageSize) => ['temporary-funds', 'list', 'v2', { filters, page, pageSize }],
  },
  // Accounts
  accounts: {
    all: ['accounts'],
    list: (accountType, pageLimit) => ['accounts', 'list', accountType, pageLimit],
  },
  // Medical expenses
  medicalExpenses: {
    all: ['medical-expenses'],
    listV2: (filters, page, pageSize) => ['medical-expenses', 'list', 'v2', { filters, page, pageSize }],
  },
  // Case expenses
  caseExpenses: {
    all: ['case-expenses'],
    listV2: (filters, page, pageSize) => ['case-expenses', 'list', 'v2', { filters, page, pageSize }],
  },
  // Batch billing
  batchBilling: {
    all: ['batch-billing'],
    list: (filters, page, pageSize) => ['batch-billing', 'list', { filters, page, pageSize }],
  },
};
